/**
 * Shopping cart store for domain purchases
 * P20250925-114 — Cart with lease support
 * Persists to localStorage for guest users
 */

export interface CartItem {
  id: string;
  domain: string;
  tld: string;
  binPrice: number;
  lease?: boolean;
  leaseTerm?: number; // months
  leaseMonthly?: number; // monthly payment amount
}

const CART_STORAGE_KEY = 'ns_cart_items';

// Event for cart updates
export const CART_UPDATED_EVENT = 'cart-updated';

function dispatchCartUpdate() {
  window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
}

/**
 * Get all cart items
 */
export function getCartItems(): CartItem[] {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return [];

    const items = JSON.parse(stored);
    return Array.isArray(items) ? items : [];
  } catch (error) {
    console.error('Error reading cart from localStorage:', error);
    return [];
  }
}

/**
 * Get cart count
 */
export function getCartCount(): number {
  return getCartItems().length;
}

/**
 * Get cart subtotal
 */
export function getCartSubtotal(): number {
  const items = getCartItems();
  return items.reduce((sum, item) => sum + item.binPrice, 0);
}

/**
 * Check if domain is in cart
 */
export function isInCart(domain: string): boolean {
  const items = getCartItems();
  return items.some((item) => item.domain.toLowerCase() === domain.toLowerCase());
}

/**
 * Add item to cart
 * Returns true if added, false if already exists
 */
export function addToCart(item: Omit<CartItem, 'id'>): boolean {
  const items = getCartItems();

  // Check for duplicates
  if (items.some((i) => i.domain.toLowerCase() === item.domain.toLowerCase())) {
    return false;
  }

  const newItem: CartItem = {
    id: `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    ...item,
  };

  items.push(newItem);

  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    dispatchCartUpdate();
    return true;
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
    return false;
  }
}

/**
 * Get current cart items (alias for getCartItems)
 */
export function getCart(): CartItem[] {
  return getCartItems();
}

/**
 * Remove item from cart by domain name
 */
export function removeFromCart(domain: string): boolean {
  const items = getCartItems();
  const filtered = items.filter((item) => item.domain.toLowerCase() !== domain.toLowerCase());

  if (filtered.length === items.length) {
    return false; // Item not found
  }

  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(filtered));
    dispatchCartUpdate();
    return true;
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
    return false;
  }
}

/**
 * Clear all items from cart
 */
export function clearCart(): number {
  const prevCount = getCartCount();

  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    dispatchCartUpdate();
    return prevCount;
  } catch (error) {
    console.error('Error clearing cart:', error);
    return 0;
  }
}
